/* ==========================================================================================
   Commercial Equipment Online 2026 · ce-home.js · the home page behaviours (home builder, 09-10-2026)
   Loaded deferred by the ce-home-* sections (the browser runs it once; each part binds only new markup).
   No dependencies. Every part respects prefers-reduced-motion and works (or stays still) without it.
     hero      [data-cehm-hero]      slides that fade: a soft 45-degree arrival, tabs, autoplay (paused under the
                                     pointer / focus / a hidden tab / off screen; stops once a shopper picks a slide),
                                     swipe, arrow keys on the tabs; photos 2+ load after the page
     deals     [data-cehm-deals]     countdown to data-end; hides the strip outside data-start .. data-end
     copy      [data-cehm-copy]      copies a discount code (the visitor's own click) and says "Copied"
     featured  [data-cehm-featured]  picks the featured brands at random on every load, weighted (data-weight)
     calc      [data-cehm-calc]      SilverChef weekly rent = price x rate x 12 / 52 (the product page's sum)
     map       [data-cehm-map]       draws the delivery dots from the JSON asset and lights them up in view
     popup     [data-cehm-pop]       the first-visit popup (once per visitor, never over the cart)
   ========================================================================================== */
(() => {
  'use strict';
  const W = window, D = document;
  if (W.__cehmHome) return;                         // every home section loads this file; run it once
  W.__cehmHome = true;
  const mq = W.matchMedia ? W.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  const reduce = () => mq.matches;
  const qsa = (root, sel) => Array.prototype.slice.call((root || D).querySelectorAll(sel));
  const once = (el, key) => { const k = '__cehm_' + key; if (el[k]) return false; el[k] = 1; return true; };
  const store = {
    get(k) { try { return W.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { W.localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };
  const money = (n) => '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /* ---- hero (stage 3): slides that fade into a soft colour; tabs, autoplay, swipe, photos 2+ after the page ---- */
  const promote = (slide) => {
    if (!slide || slide.__cehmLoaded) return;
    slide.__cehmLoaded = true;
    qsa(slide, '[data-src], [data-srcset]').forEach((el) => {
      if (el.dataset.srcset) { el.setAttribute('srcset', el.dataset.srcset); el.removeAttribute('data-srcset'); }
      if (el.dataset.src) { el.setAttribute('src', el.dataset.src); el.removeAttribute('data-src'); }
    });
  };
  const hero = (root) => qsa(root, '[data-cehm-hero]').forEach((h) => {
    if (!once(h, 'hero')) return;
    const slides = qsa(h, '[data-cehm-slide]');
    const tabs = qsa(h, '[data-cehm-dot]');
    const stage = h.querySelector('.cehm-hero__stage');
    const tone = (sl) => {
      h.classList.toggle('is-dark', !!sl && sl.classList.contains('cehm-slide--white'));
      h.classList.toggle('is-right', !!sl && sl.classList.contains('cehm-slide--right'));
      h.classList.toggle('has-band', !!sl && sl.classList.contains('has-band')); // Tommy 10-10: the tabs lift above a logo band
    };
    tone(slides[0]);
    if (slides.length < 2 || !stage) return;
    // the other slides' photos load once the page has (never before slide 1, the page's main picture)
    const later = () => { const go = () => slides.forEach(promote); if (W.requestIdleCallback) W.requestIdleCallback(go, { timeout: 2500 }); else setTimeout(go, 600); };
    if (D.readyState === 'complete') later(); else W.addEventListener('load', later, { once: true });
    const speed = (parseFloat(h.dataset.speed) || 7) * 1000;
    const auto = h.dataset.autoplay === 'true';
    const pauseBtn = h.querySelector('[data-cehm-pause]');
    // Tommy 10-10 ("the banners don't seem to auto change, and add a little timer so know when going to change"): the
    // mouse resting on the banner no longer holds it (it filled the first screen, so it rarely moved); the current
    // tab's pill fills as a timer (.is-timing, CSS), stands still while held (.is-held: keyboard focus inside, a
    // hidden tab, scrolled away) and the pause button stops it for good. `remain` carries a held slide's time over.
    let i = 0, timer = 0, busy = false, focus = false, seen = true, stopped = false, queued = null;
    let remain = speed, started = 0;
    h.style.setProperty('--cehm-speed', speed + 'ms');
    const hold = () => {
      if (timer) { clearTimeout(timer); timer = 0; remain = Math.max(400, remain - (Date.now() - started)); }
      h.classList.add('is-held');
    };
    const schedule = () => {
      if (!auto || stopped || reduce()) { hold(); h.classList.remove('is-timing'); return; }
      if (focus || !seen || D.hidden) { hold(); return; }
      if (timer) return;
      h.classList.add('is-timing'); h.classList.remove('is-held');
      started = Date.now();
      timer = setTimeout(() => { timer = 0; show(i + 1, 1); }, remain);
    };
    const restart = () => { clearTimeout(timer); timer = 0; remain = speed; };
    const setPause = (on) => {
      if (!pauseBtn) return;
      pauseBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      pauseBtn.setAttribute('aria-label', on ? (pauseBtn.dataset.play || 'Play') : (pauseBtn.dataset.pause || 'Pause'));
    };
    if (pauseBtn) {
      if (!auto || reduce()) pauseBtn.hidden = true;
      pauseBtn.addEventListener('click', () => {
        stopped = !stopped;
        setPause(stopped);
        if (!stopped) { restart(); }
        schedule();
      });
    }
    const setTabs = (n) => tabs.forEach((t, k) => {
      const on = k === n;
      t.classList.toggle('cehm-on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    // the slide a shopper last asked for (a request made mid-change waits for the change to finish)
    const want = () => (queued ? queued.n : i);
    const show = (n, dir, byUser) => {
      n = (n + slides.length) % slides.length;
      if (busy) {
        // fix round 10-10 (critic: two quick arrow presses left the focus on one tab and another slide shown): the
        // tabs follow at once and the slide catches up as soon as the current change ends
        if (!byUser) return;
        queued = n === i ? null : { n, dir };
        setTabs(n);
        stopped = true;
        return;
      }
      if (n === i) return;
      const from = slides[i], to = slides[n];
      promote(to);
      busy = true;
      restart();                                     // the next slide gets its full time (and a fresh timer pill)
      from.classList.remove('cehm-on', 'is-in', 'is-back');
      from.classList.add('is-out');
      to.classList.remove('is-out', 'is-in', 'is-back');
      void to.offsetWidth;                          // restart the word animations on a slide seen before
      to.classList.add('cehm-on', 'is-in');
      if (dir < 0) to.classList.add('is-back');
      to.removeAttribute('aria-hidden'); to.inert = false;
      from.setAttribute('aria-hidden', 'true'); from.inert = true;
      setTabs(n);
      tone(to);
      i = n;
      if (byUser) { stopped = true; setPause(true); } // a shopper took over: no more autoplay this visit (play resumes it)
      setTimeout(() => {
        from.classList.remove('is-out'); to.classList.remove('is-in', 'is-back'); busy = false;
        if (queued) { const q = queued; queued = null; show(q.n, q.dir, true); }
      }, reduce() ? 30 : 1150);
      schedule();
    };
    tabs.forEach((t, k) => t.addEventListener('click', () => show(k, k > want() ? 1 : -1, true)));
    const list = h.querySelector('.cehm-tabs');
    if (list) list.addEventListener('keydown', (e) => {
      const at = want();
      const keys = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: slides.length - 1 };
      if (!(e.key in keys)) return;
      e.preventDefault();
      const n = (keys[e.key] + slides.length) % slides.length;
      show(n, n > at ? 1 : -1, true);
      if (tabs[n]) tabs[n].focus();
    });
    h.addEventListener('focusin', (e) => { if (e.target === pauseBtn) return; focus = true; hold(); });
    h.addEventListener('focusout', (e) => { if (!h.contains(e.relatedTarget)) { focus = false; schedule(); } });
    D.addEventListener('visibilitychange', schedule);
    // swipe (touch / pen): sideways and longer than 44px
    let sx = 0, sy = 0, st = 0;
    // Tommy 11-10 (live): phones show no dots and no pause button (ce-home.css) - so on a phone a touch on the banner
    // is the pause: it stops the autoplay for the rest of the visit (a swipe still changes the slide)
    const phone = W.matchMedia('(max-width: 749px)');
    stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      sx = e.clientX; sy = e.clientY; st = Date.now();
      if (phone.matches && !stopped) { stopped = true; setPause(true); schedule(); }
    }, { passive: true });
    stage.addEventListener('pointerup', (e) => {
      if (e.pointerType === 'mouse' || !st) return;
      const dx = e.clientX - sx, dy = e.clientY - sy;
      st = 0;
      if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.3) show(dx < 0 ? i + 1 : i - 1, dx < 0 ? 1 : -1, true);
    }, { passive: true });
    if ('IntersectionObserver' in W) {
      new IntersectionObserver((es) => { seen = es[0].isIntersecting; schedule(); }, { threshold: 0.25 }).observe(h);
      // Tommy 11-10 ("go banner"): on a phone the chat bubble sat on "Get a quote" - it is out of sight (ce-home.css
      // html.ce-chat-tuck) while any slide's buttons are on screen, and back as soon as they scroll away
      const ctas = qsa(h, '.cehm-slide__ctas'), on = new Set();
      const tuck = () => D.documentElement.classList.toggle('ce-chat-tuck', phone.matches && on.size > 0);
      if (ctas.length) {
        const io = new IntersectionObserver((es) => { es.forEach((e) => (e.isIntersecting ? on.add(e.target) : on.delete(e.target))); tuck(); });
        ctas.forEach((c) => io.observe(c));
        if (phone.addEventListener) phone.addEventListener('change', tuck);
      }
    }
    if (mq.addEventListener) mq.addEventListener('change', schedule);
    schedule();
  });

  /* ---- deals: the countdown, and the strip hides itself outside its dates ---- */
  const deals = (root) => qsa(root, '[data-cehm-deals]').forEach((sec) => {
    if (!once(sec, 'deals')) return;
    const editor = !!(W.Shopify && W.Shopify.designMode);
    const start = sec.dataset.start ? Date.parse(sec.dataset.start) : 0;
    const end = sec.dataset.end ? Date.parse(sec.dataset.end) : 0;
    const hide = () => { if (editor) return; const wrap = sec.closest('.shopify-section') || sec; wrap.hidden = true; };
    const now = Date.now();
    if ((start && now < start) || (end && now > end)) { hide(); return; }
    // the cards' "Ends in N days" (the page may have been cached since it was printed)
    const endsEls = [...sec.querySelectorAll('[data-cehm-ends]')];
    const dayW = sec.dataset.dayWord || '1 day', daysW = sec.dataset.daysWord || 'days';
    const endsTick = () => {
      if (!end || !endsEls.length) return;
      // whole days, rounded DOWN like the countdown beside the cards (fix round 10-10: "22 DAYS" vs "Ends in 23 days")
      const d = Math.max(1, Math.floor((end - Date.now()) / 86400000));
      const txt = d === 1 ? dayW : d + ' ' + daysW;
      endsEls.forEach((el) => { const s = el.dataset.cehmEnds.replace('[days]', txt); if (el.textContent !== s) el.textContent = s; });
    };
    endsTick();
    const c = sec.querySelector('[data-cehm-count]');
    if (!c || !end) return;
    const u = {};
    ['d', 'h', 'm', 's'].forEach((k) => { u[k] = c.querySelector('[data-u="' + k + '"]'); });
    const pad = (n) => (n < 10 ? '0' : '') + n;
    // the numbers change their text in place (fix round 10-10): setting textContent swaps the text node, and a node
    // added anywhere makes the browser re-check the theme's body:has(...) rules, once a second
    const put = (el, v) => {
      if (!el) return;
      const tn = el.firstChild;
      if (tn && tn.nodeType === 3 && !tn.nextSibling) { if (tn.data !== v) tn.data = v; } else if (el.textContent !== v) el.textContent = v;
    };
    let t = 0;
    const tick = () => {
      const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      if (left <= 0) { clearInterval(t); hide(); return; }
      const d = Math.floor(left / 86400), hh = Math.floor((left % 86400) / 3600), mm = Math.floor((left % 3600) / 60), ss = left % 60;
      put(u.d, String(d));
      put(u.h, pad(hh));
      put(u.m, pad(mm));
      put(u.s, pad(ss));
      if (ss === 0) endsTick();
    };
    tick();
    t = setInterval(tick, 1000);
  });

  /* ---- copy a code ---- */
  const copyText = (txt) => {
    if (navigator.clipboard && W.isSecureContext) return navigator.clipboard.writeText(txt);
    return new Promise((res, rej) => {
      const ta = D.createElement('textarea');
      ta.value = txt; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      D.body.appendChild(ta); ta.select();
      try { D.execCommand('copy') ? res() : rej(); } catch (e) { rej(e); }
      ta.remove();
    });
  };
  const copy = (root) => qsa(root, '[data-cehm-copy]').forEach((b) => {
    if (!once(b, 'copy')) return;
    const wrap = b.querySelector('[data-cehm-copy-label]');
    const label = wrap ? wrap.querySelector('span') : null;
    const was = label ? label.textContent : '';
    const done = (wrap && wrap.dataset.done) || 'Copied';
    b.addEventListener('click', () => {
      copyText(b.dataset.cehmCopy || '').then(() => {
        b.classList.add('is-copied');
        if (label) label.textContent = done || 'Copied';
        setTimeout(() => { b.classList.remove('is-copied'); if (label) label.textContent = was; }, 2200);
      }).catch(() => { /* the code stays on screen to type */ });
    });
  });

  /* ---- featured brands: a weighted draw on every load ---- */
  const featured = (root) => qsa(root, '[data-cehm-featured]').forEach((w) => {
    if (!once(w, 'featured')) return;
    const slots = qsa(w, '.cehm-fb-slot');
    const n = parseInt(w.dataset.show, 10) || 3;
    if (slots.length <= n) return;
    const pool = slots.map((s) => ({ s, w: Math.max(1, parseFloat(s.dataset.weight) || 1) }));
    const pick = [];
    while (pick.length < n && pool.length) {
      const total = pool.reduce((a, p) => a + p.w, 0);
      let r = Math.random() * total, k = 0;
      for (; k < pool.length - 1; k++) { r -= pool[k].w; if (r <= 0) break; }
      pick.push(pool.splice(k, 1)[0].s);
    }
    slots.forEach((s) => { s.hidden = pick.indexOf(s) < 0; });
    const list = slots[0].parentNode;
    pick.forEach((s, idx) => { list.appendChild(s); s.style.setProperty('--i', idx); s.classList.add('is-new'); });
  });

  /* ---- SilverChef weekly rent ---- */
  const calc = (root) => qsa(root, '[data-cehm-calc]').forEach((c) => {
    if (!once(c, 'calc')) return;
    const inp = c.querySelector('[data-cehm-calc-input]');
    const pOut = c.querySelector('[data-cehm-calc-price]');
    const wOut = c.querySelector('[data-cehm-calc-week]');
    const rate = parseFloat(c.dataset.rate) || 0.055;
    if (!inp) return;
    const upd = () => {
      const v = parseFloat(inp.value) || 0;
      const min = parseFloat(inp.min) || 0, max = parseFloat(inp.max) || 1;
      if (pOut) pOut.textContent = '$' + Math.round(v).toLocaleString('en-AU');
      if (wOut) wOut.textContent = money(Math.round(v * rate * 12 / 52 * 100) / 100);
      inp.style.setProperty('--p', ((v - min) / (max - min) * 100).toFixed(1) + '%');
    };
    inp.addEventListener('input', upd);
    upd();
  });

  /* ---- the delivery map ---- */
  const SVGNS = 'http://www.w3.org/2000/svg';
  const map = (root) => qsa(root, '[data-cehm-map]').forEach((m) => {
    if (!once(m, 'map')) return;
    const g = m.querySelector('[data-cehm-map-dots]');
    const url = m.dataset.dots;
    if (!g || !url) return;
    const draw = (dots) => {
      // the biggest regions first, the rest in a random sprinkle
      const order = dots.slice().sort((a, b) => (b.w - a.w) || (Math.random() - 0.5));
      const spread = Math.min(3200, 60 * order.length);
      const frag = D.createDocumentFragment();
      order.forEach((d, k) => {
        const delay = Math.round(k === 0 ? 0 : spread * Math.pow(k / order.length, 0.85));
        const r = 2.6 + d.w * 1.05;
        const grp = D.createElementNS(SVGNS, 'g');
        grp.style.setProperty('--d', delay);
        const t = D.createElementNS(SVGNS, 'title');
        t.textContent = d.n + ', ' + d.s;
        grp.appendChild(t);
        const glow = D.createElementNS(SVGNS, 'circle');
        glow.setAttribute('class', 'cehm-map__glow');
        glow.setAttribute('cx', d.x); glow.setAttribute('cy', d.y); glow.setAttribute('r', (r * 2.6).toFixed(1));
        grp.appendChild(glow);
        if (d.w >= 5 && k < 14) {
          const ring = D.createElementNS(SVGNS, 'circle');
          ring.setAttribute('class', 'cehm-map__ring');
          ring.setAttribute('cx', d.x); ring.setAttribute('cy', d.y); ring.setAttribute('r', (r * 1.4).toFixed(1));
          grp.style.setProperty('--d', delay + (k % 7) * 420);
          grp.appendChild(ring);
        }
        const dot = D.createElementNS(SVGNS, 'circle');
        dot.setAttribute('class', 'cehm-map__dot');
        dot.setAttribute('cx', d.x); dot.setAttribute('cy', d.y); dot.setAttribute('r', r.toFixed(1));
        grp.appendChild(dot);
        frag.appendChild(grp);
      });
      g.appendChild(frag);
    };
    const light = () => {
      if (reduce() || !('IntersectionObserver' in W)) { m.classList.add('is-lit'); return; }
      const io2 = new IntersectionObserver((es) => {
        if (!es[0].isIntersecting) return;
        io2.disconnect();
        requestAnimationFrame(() => m.classList.add('is-lit'));
      }, { threshold: 0.35 });
      io2.observe(m);
    };
    const load = () => fetch(url, { credentials: 'omit' }).then((r) => r.json()).then((j) => { draw(j.dots || []); light(); }).catch(() => {});
    if ('IntersectionObserver' in W) {
      const io = new IntersectionObserver((es) => { if (!es[0].isIntersecting) return; io.disconnect(); load(); }, { rootMargin: '400px 0px' });
      io.observe(m);
    } else load();
  });

  /* ---- the first-visit popup ---- */
  const KEY = 'ce-home-pop';
  const cartOpen = () => !!D.querySelector('cart-drawer.active, cart-drawer[open], .ce-cart-drawer.is-open, #CartDrawer.active') || D.body.classList.contains('ce-drawer-open') || /^\/cart/.test(location.pathname);
  const popup = (root) => qsa(root, '[data-cehm-pop]').forEach((host) => {
    if (!once(host, 'pop')) return;
    const dlg = host.querySelector('[data-cehm-pop-dialog]');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    const editor = host.hasAttribute('data-editor');
    const result = host.querySelector('[data-cehm-pop-done], [data-cehm-pop-error]');
    let timer = 0;
    const open = (force) => {
      if (dlg.open) return;
      if (!force && (cartOpen() || D.querySelector('dialog[open]'))) return;
      try { dlg.showModal(); } catch (e) { return; }
      if (!editor) store.set(KEY, 'shown ' + new Date().toISOString().slice(0, 10));
      cleanup();
    };
    const close = () => { if (dlg.open) dlg.close(); };
    qsa(host, '[data-cehm-pop-close]').forEach((b) => b.addEventListener('click', close));
    // a click on the dimmed page around the card closes it
    dlg.addEventListener('click', (e) => { if (e.target === dlg) close(); });
    // never over the cart: close if the cart drawer opens (Dawn marks it .active)
    const cd = D.querySelector('cart-drawer');
    if (cd && 'MutationObserver' in W) new MutationObserver(() => { if (cartOpen()) close(); }).observe(cd, { attributes: true, attributeFilter: ['class', 'open'] });
    const onScroll = () => {
      const max = D.documentElement.scrollHeight - W.innerHeight;
      if (max > 0 && W.scrollY / max >= (parseFloat(host.dataset.scroll) || 35) / 100) open();
    };
    const cleanup = () => { clearTimeout(timer); W.removeEventListener('scroll', onScroll); };
    if (result) {                                   // back from signing up: show the code (or the error)
      if (host.querySelector('[data-cehm-pop-done]')) store.set(KEY, 'joined');
      open(true);
      return;
    }
    if (editor) {
      D.addEventListener('shopify:section:select', (e) => { if (e.target.contains(host)) open(true); });
      D.addEventListener('shopify:section:deselect', (e) => { if (e.target.contains(host)) close(); });
      return;
    }
    if (store.get(KEY)) return;                    // once per visitor
    timer = setTimeout(() => open(), (parseFloat(host.dataset.delay) || 9) * 1000);
    W.addEventListener('scroll', onScroll, { passive: true });
  });

  const init = (root) => { hero(root); deals(root); copy(root); featured(root); calc(root); map(root); popup(root); };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D)); else init(D);
  D.addEventListener('shopify:section:load', (e) => init(e.target));
})();
